import { Badge } from "@/components/ui/badge";
import { RuleChip } from "@/components/ui/rule-chip";
import { usePersistentState } from "@/hooks/use-persistent-state";
import { fmtRub } from "@/lib/format";
import {
	formatMessage,
	getRuleDisplayLabel,
	reconciliationResultMessages,
} from "@/services/messages";
import type { ActSourceRef, WorkOrderSourceRef } from "@efes/shared";
import { AlertTriangle, ChevronDown, ChevronRight, XCircle } from "lucide-react";
import { useMemo } from "react";
import { DiscrepancyRelatedSection } from "./DiscrepancyRelatedSection";
import { SeverityBadge } from "./SeverityBadge";
import { cardClassName, railColor } from "./lib/card-style";
import { levelLabel } from "./lib/level-label";
import type { DiscrepancyCardViewModel } from "./lib/view-model";

/**
 * Карточка для расхождения уровня `grand-total` (RC-G1, фича 024).
 *
 * Принципиально отличается от matrix/aggregated:
 * - matrix дизайнилась под одну позицию с двумя источниками (обе стороны
 *   относятся к одному коду);
 * - grand-total — это сводное расхождение по ВСЕМУ набору. trace.workOrderSources
 *   содержит все wo-строки (lineType=1), trace.actSources — все строки актов.
 *   Их нельзя складывать в одну таблицу и брать workName из первой строки —
 *   это вводит пользователя в заблуждение.
 *
 * Layout:
 * - header: RC-G1 chip + «Гранд-тотал по набору» (без workName);
 * - subtitle: Σ wo vs Σ act, Δ ±, %;
 * - 3-ячеечный σ-strip: Σ наряды / Σ акты / Δ;
 * - таблица «Расходящиеся коды» (suspect): группировка по коду, по строкам wo/act;
 * - сворачиваемая таблица «Совпавшие позиции».
 */

export interface DiscrepancyGrandTotalCardProps {
	viewModel: DiscrepancyCardViewModel;
	runId?: string;
	highlighted?: boolean;
	onRelatedClick?: (targetId: string) => void;
}

interface CodeRow {
	code: string | null;
	woSum: number;
	actSum: number;
	woCount: number;
	actCount: number;
}

export function DiscrepancyGrandTotalCard({
	viewModel,
	runId,
	highlighted = false,
	onRelatedClick,
}: DiscrepancyGrandTotalCardProps) {
	const messages = reconciliationResultMessages.redesign;
	// Sprint 6 / C1.4: при `runId === undefined` пустой ключ — паттерн из
	// usePinnedDiscrepancies (no-op localStorage, без засорения «no-run»-ключа).
	const persistKey = runId ? `recon:gt-matched-open:${runId}:${viewModel.id}` : "";
	const [matchedOpen, setMatchedOpen] = usePersistentState<boolean>(persistKey, false);

	// expected/actual приходят как строки в violation.expected/violation.actual
	// (persistence пишет `String(comparison.expected)` и не восстанавливает
	// `trace.comparison` обратно — см. apps/api/.../persistence.ts:256-267).
	// Поэтому парсим строки сами и считаем diff/diffPercent.
	const firstViolation = viewModel.violations[0];
	const ruleLabel = firstViolation ? getRuleDisplayLabel(firstViolation.ruleId) : "RC-G1";
	const expected = numericOr(firstViolation?.expected, 0);
	const actual = numericOr(firstViolation?.actual, 0);
	const diff = actual - expected;
	const diffPercent = expected === 0 ? 0 : (diff / expected) * 100;

	const subtitle = formatMessage(messages.grand_total_subtitle_template, {
		expected: fmtRub(expected).replace(/\s?₽$/, "").trim(),
		actual: fmtRub(actual).replace(/\s?₽$/, "").trim(),
		diff: signed(diff),
		diffPercent: diffPercent.toFixed(2),
	});

	// Группируем по коду (rateNumber для wo, code для act). isSuspect разделяет
	// расходящиеся группы от совпавших.
	const { suspectRows, matchedRows } = useMemo(
		() => groupByCode(viewModel.workOrderSources, viewModel.actSources),
		[viewModel.workOrderSources, viewModel.actSources],
	);

	return (
		<article
			id={`discrepancy-${viewModel.id}`}
			aria-label={viewModel.summary}
			className={cardClassName(viewModel.severity, highlighted)}
		>
			<div className="flex">
				<div className={`w-1 ${railColor(viewModel.severity)}`} aria-hidden="true" />
				<div className="min-w-0 flex-1">
					{/* Header */}
					<header className="flex items-start gap-3 px-4 pt-3 pb-2">
						{viewModel.severity === "error" ? (
							<XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
						) : (
							<AlertTriangle
								className="mt-0.5 h-4 w-4 shrink-0 text-yellow-500"
								aria-hidden="true"
							/>
						)}
						<div className="min-w-0 flex-1">
							<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
								<RuleChip>{ruleLabel}</RuleChip>
								<span className="text-slate-300">·</span>
								<span className="text-[11px] text-slate-500">{levelLabel(viewModel.level)}</span>
								<span className="ml-auto">
									<SeverityBadge severity={viewModel.severity} />
								</span>
							</div>
							<h3 className="mt-1 text-[14px] font-semibold leading-snug text-slate-900 text-pretty">
								{messages.grand_total_title}
							</h3>
							<p className="mt-0.5 text-[12px] text-slate-500">{subtitle}</p>
						</div>
					</header>

					{/* 3-ячеечный σ-strip: Σ наряды / Σ акты / Δ */}
					<div className="mx-4 mb-3 grid grid-cols-3 gap-px overflow-hidden rounded-md bg-slate-200 text-xs ring-1 ring-slate-200">
						<SigmaCell label={messages.grand_total_sigma_wo}>
							<span className="font-mono text-[15px] font-semibold tabular-nums text-slate-900">
								{fmtRub(expected)}
							</span>
						</SigmaCell>
						<SigmaCell label={messages.grand_total_sigma_act}>
							<span className="font-mono text-[15px] font-semibold tabular-nums text-slate-900">
								{fmtRub(actual)}
							</span>
						</SigmaCell>
						<SigmaCell label={messages.grand_total_sigma_diff}>
							<span
								className={`font-mono text-[15px] font-semibold tabular-nums ${
									Math.abs(diff) < 0.5 ? "text-slate-700" : "text-red-700"
								}`}
							>
								{signed(diff)} ₽
							</span>
						</SigmaCell>
					</div>

					{/* Расходящиеся коды (suspect) */}
					<section className="border-t border-slate-100 bg-rose-50/30">
						<div className="px-4 py-2 text-[12px] font-medium text-slate-700">
							{formatMessage(messages.grand_total_suspect_section_template, {
								n: suspectRows.length,
							})}
						</div>
						{suspectRows.length === 0 ? (
							<p className="px-4 pb-3 text-[12px] text-slate-500">
								{messages.grand_total_suspect_empty}
							</p>
						) : (
							<CodeTable rows={suspectRows} highlight />
						)}
					</section>

					{/* Совпавшие коды (collapsed) */}
					{matchedRows.length > 0 ? (
						<section className="border-t border-slate-100 bg-slate-50/40">
							<button
								type="button"
								onClick={() => setMatchedOpen((o) => !o)}
								aria-expanded={matchedOpen}
								className="flex w-full items-center gap-2 px-4 py-2 text-[12px] font-medium text-slate-600 hover:bg-slate-50"
							>
								{matchedOpen ? (
									<ChevronDown className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
								) : (
									<ChevronRight className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
								)}
								{formatMessage(
									matchedOpen
										? messages.grand_total_matched_expanded_template
										: messages.grand_total_matched_collapsed_template,
									{ n: matchedRows.length },
								)}
							</button>
							{matchedOpen ? <CodeTable rows={matchedRows} /> : null}
						</section>
					) : null}

					{/* Drill-down related */}
					{viewModel.hasRelated && onRelatedClick ? (
						<div className="px-4 pb-3">
							<DiscrepancyRelatedSection
								relatedIds={viewModel.relatedDiscrepancyIds}
								onRelatedClick={onRelatedClick}
							/>
						</div>
					) : null}
				</div>
			</div>
		</article>
	);
}

function CodeTable({ rows, highlight = false }: { rows: CodeRow[]; highlight?: boolean }) {
	const messages = reconciliationResultMessages.redesign;
	return (
		<div className="overflow-x-auto px-4 pb-3">
			<table className="w-full text-[12px]">
				<thead>
					<tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
						<th className="py-1.5 pr-3 font-medium">{messages.grand_total_table_col_code}</th>
						<th className="py-1.5 pr-3 text-right font-medium">
							{messages.grand_total_table_col_wo}
						</th>
						<th className="py-1.5 pr-3 text-right font-medium">
							{messages.grand_total_table_col_act}
						</th>
						<th className="py-1.5 pr-3 text-right font-medium">
							{messages.grand_total_table_col_delta}
						</th>
						<th className="py-1.5 text-right font-medium">
							{messages.grand_total_table_col_lines}
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((r) => {
						const rowDiff = r.actSum - r.woSum;
						const isMismatch = Math.abs(rowDiff) >= 0.5;
						return (
							<tr
								key={r.code ?? "__uncoded__"}
								className={`border-t border-slate-100 ${
									highlight && isMismatch ? "bg-rose-50/40" : ""
								}`}
							>
								<td className="py-1.5 pr-3 font-mono text-slate-700">
									{r.code ?? (
										<span className="text-slate-400">{messages.grand_total_uncoded_label}</span>
									)}
								</td>
								<td className="py-1.5 pr-3 text-right font-mono tabular-nums text-slate-700">
									{fmtRub(r.woSum)}
								</td>
								<td className="py-1.5 pr-3 text-right font-mono tabular-nums text-slate-700">
									{fmtRub(r.actSum)}
								</td>
								<td
									className={`py-1.5 pr-3 text-right font-mono tabular-nums ${
										isMismatch ? "font-semibold text-red-700" : "text-slate-400"
									}`}
								>
									{isMismatch ? `${signed(rowDiff)} ₽` : "—"}
								</td>
								<td className="py-1.5 text-right font-mono text-[11px] tabular-nums text-slate-500">
									{r.woCount}/{r.actCount}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

function SigmaCell({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="bg-white px-3 py-2">
			<div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
			<div className="mt-0.5">{children}</div>
		</div>
	);
}

// Sprint 6 / C11.2: SeverityBadge вынесен в общий модуль ./SeverityBadge.

function numericOr(v: string | number | null | undefined, fallback: number): number {
	if (typeof v === "number") return v;
	if (typeof v === "string") {
		const n = Number(v);
		return Number.isFinite(n) ? n : fallback;
	}
	return fallback;
}

function signed(n: number): string {
	const abs = Math.abs(n);
	const formatted = fmtRub(abs).replace(/\s?₽$/, "").trim();
	if (Math.abs(n) < 0.5) return formatted;
	return n >= 0 ? `+${formatted}` : `−${formatted}`;
}

/**
 * Группирует sources по коду и разделяет на расходящиеся / совпавшие.
 *
 * Семантика:
 * - rateNumber из wo и code из act считаются одним ключом (как и в
 *   симметричном walk на backend, методология § 5.2).
 * - Бескодовые сводятся в один ряд `(без кода)`.
 * - `actSum` = Σ (salaryTotal + extraLaborTotal) — это полный вклад строки в
 *   гранд-тотал актов (см. rc-g1-grand-total.ts:53-57). БЕЗ extraLabor суммы по
 *   группам не сходятся с шапкой (бескодовые extraLabor особенно крупные).
 *   `extraLaborTotal` приходит начиная с фичи 024-grand-total fix; для старых
 *   снэпшотов трактуется как 0.
 * - Разделение `mismatch` vs `matched` — по фактической |actSum - woSum| ≥ 0.5,
 *   а не по `isSuspect`. Backend помечает suspect только для **двусторонних
 *   групп с непустым кодом** — но односторонние и бескодовые расхождения тоже
 *   нужно показывать в «расходящихся» (иначе шапка не сходится с таблицей).
 */
function groupByCode(
	wo: ReadonlyArray<WorkOrderSourceRef>,
	acts: ReadonlyArray<ActSourceRef>,
): { suspectRows: CodeRow[]; matchedRows: CodeRow[] } {
	type Bucket = { woSum: number; actSum: number; woCount: number; actCount: number };
	const map = new Map<string, Bucket>();
	const codeOrder: (string | null)[] = [];
	const seen = new Set<string>();

	function ensure(code: string | null): Bucket {
		const key = code ?? "__uncoded__";
		let b = map.get(key);
		if (!b) {
			b = { woSum: 0, actSum: 0, woCount: 0, actCount: 0 };
			map.set(key, b);
			if (!seen.has(key)) {
				seen.add(key);
				codeOrder.push(code);
			}
		}
		return b;
	}

	for (const l of wo) {
		const b = ensure(normalizeCode(l.rateNumber));
		b.woSum += l.amount;
		b.woCount += 1;
	}
	for (const a of acts) {
		const b = ensure(normalizeCode(a.code));
		b.actSum += a.salaryTotal + (a.extraLaborTotal ?? 0);
		b.actCount += 1;
	}

	const suspectRows: CodeRow[] = [];
	const matchedRows: CodeRow[] = [];
	for (const code of codeOrder) {
		const key = code ?? "__uncoded__";
		const b = map.get(key);
		if (!b) continue;
		const row: CodeRow = {
			code,
			woSum: b.woSum,
			actSum: b.actSum,
			woCount: b.woCount,
			actCount: b.actCount,
		};
		if (Math.abs(b.actSum - b.woSum) >= 0.5) suspectRows.push(row);
		else matchedRows.push(row);
	}

	suspectRows.sort((a, b) => Math.abs(b.actSum - b.woSum) - Math.abs(a.actSum - a.woSum));
	matchedRows.sort((a, b) => (a.code ?? "").localeCompare(b.code ?? "", "ru"));

	return { suspectRows, matchedRows };
}

function normalizeCode(c: string | null | undefined): string | null {
	if (c === null || c === undefined) return null;
	const t = c.trim();
	return t === "" ? null : t;
}
